'use client';

import { useState } from 'react';
import { LEGACY_LOGO_SOURCES } from './logo-assets';
import {
  EXPERIENCE_BROWSER_BRAND_ASSETS,
  type ExperienceBrowserBrandAssetVariant,
} from '@statura/config';

import { cn } from '../lib/utils';
import { useExperienceModeBoundary, type ExperienceRenderMode } from './experience-mode-boundary';

export type LogoVariant = 'full' | ExperienceBrowserBrandAssetVariant;

export interface LogoProps {
  variant?: LogoVariant;
  className?: string;
  width?: number;
  height?: number;
  /** Empty alt/accessible name when adjacent visible product copy already names Statura Care. */
  decorative?: boolean;
}

/** Candidate mode is inherited from the governed boundary and cannot be caller-overridden. */
export type GovernedCandidateLogoProps = LogoProps;

export interface ResolvedLogoSize {
  width: number;
  height: number;
  clearSpacePx: number;
}

export interface ResolvedLegacyLogoSize {
  width: number;
  height: number;
}

/** Exact pre-candidate sizing contract retained for flag-off and legacy shells. */
export function resolveLegacyLogoSize(
  variant: LogoVariant,
  width?: number,
  height?: number,
): ResolvedLegacyLogoSize {
  const legacyHeight = height ?? 36;
  const isIcon = variant === 'icon' || variant === 'monochrome';
  return {
    width: isIcon
      ? Math.min(width ?? 40, height ?? 40)
      : (width ?? Math.round(legacyHeight * 2.67)),
    height: isIcon ? Math.min(width ?? 40, height ?? 40) : legacyHeight,
  };
}

export function resolveCandidateLogoVariant(
  variant: LogoVariant,
  mode: ExperienceRenderMode,
): ExperienceBrowserBrandAssetVariant {
  if (variant !== 'full') return variant;
  if (mode === 'dark') return 'full-dark';
  if (mode === 'high_contrast') return 'monochrome';
  return 'full-light';
}

export function canRenderGovernedCandidateLogo(
  governedBoundary: boolean,
  environment: string | undefined,
): boolean {
  return governedBoundary && (environment === 'development' || environment === 'test');
}

export function resolveLogoSize(
  variant: ExperienceBrowserBrandAssetVariant,
  width?: number,
  height?: number,
): ResolvedLogoSize {
  const asset = EXPERIENCE_BROWSER_BRAND_ASSETS.variants[variant];
  const requestedWidth = Number.isFinite(width) && (width ?? 0) > 0 ? width : undefined;
  const requestedHeight = Number.isFinite(height) && (height ?? 0) > 0 ? height : undefined;
  const defaultHeight = variant === 'icon' || variant === 'monochrome' ? 40 : 36;
  const scale = Math.max(
    (requestedWidth ?? 0) / asset.dimensions.width,
    (requestedHeight ?? defaultHeight) / asset.dimensions.height,
    asset.minimumRenderedSize.widthPx / asset.dimensions.width,
    asset.minimumRenderedSize.heightPx / asset.dimensions.height,
  );
  const resolvedWidth = Math.round(asset.dimensions.width * scale * 100) / 100;
  const resolvedHeight = Math.round(asset.dimensions.height * scale * 100) / 100;
  return {
    width: resolvedWidth,
    height: resolvedHeight,
    clearSpacePx:
      Math.round(Math.min(resolvedWidth, resolvedHeight) * asset.clearSpace.minimumFraction * 100) /
      100,
  };
}

function accessibleText(
  asset: (typeof EXPERIENCE_BROWSER_BRAND_ASSETS.variants)[ExperienceBrowserBrandAssetVariant],
  decorative: boolean,
) {
  return decorative
    ? asset.accessibility.adjacentProductNameAccessibleName
    : asset.accessibility.standaloneAccessibleName;
}

/** Exact manifest-attributed legacy renderer. Candidate bytes are unreachable through this API. */
export function Logo({
  variant = 'full',
  className,
  width,
  height,
  decorative = false,
}: LogoProps) {
  const manifestVariant = variant === 'full' ? 'full-light' : variant;
  const asset = EXPERIENCE_BROWSER_BRAND_ASSETS.variants[manifestVariant];
  const legacySize = resolveLegacyLogoSize(variant, width, height);
  const source = asset.legacySrc;
  const [failedSource, setFailedSource] = useState<string | null>(null);
  const failed = failedSource === source;
  const alternativeText = accessibleText(asset, decorative);

  const manifestAttributes = {
    'data-brand-asset-revision': EXPERIENCE_BROWSER_BRAND_ASSETS.assetRevision,
    'data-brand-asset-sha256': asset.sha256,
    'data-brand-variant': manifestVariant,
    'data-brand-render-mode': 'legacy',
  } as const;

  return (
    <div
      className={cn('flex shrink-0 items-center', className)}
      style={{ height: legacySize.height }}
      {...manifestAttributes}
      data-brand-clear-space-px="0"
    >
      {source === null || failed ? (
        <span
          role="img"
          aria-label={decorative ? undefined : asset.accessibility.standaloneAccessibleName}
          aria-hidden={decorative || undefined}
          className="inline-flex items-center justify-center font-heading font-bold text-statura-ink"
          style={{ width: legacySize.width, height: legacySize.height }}
          data-logo-fallback={
            source === null ? 'legacy_variant_unavailable' : 'legacy_asset_load_failure'
          }
        >
          {asset.accessibility.imageFailureFallbackText}
        </span>
      ) : (
        <img
          src={LEGACY_LOGO_SOURCES[source] ?? source}
          loading="eager"
          fetchPriority="high"
          alt={alternativeText}
          aria-hidden={decorative || undefined}
          width={legacySize.width}
          height={legacySize.height}
          onError={() => setFailedSource(source)}
          style={{
            width: legacySize.width,
            height: legacySize.height,
            objectFit: 'contain',
          }}
        />
      )}
    </div>
  );
}

/**
 * Evidence/lab-only candidate renderer. It requires a governed mode boundary
 * and refuses candidate bytes in production even if a caller imports it.
 */
export function GovernedCandidateLogo({
  variant = 'full',
  className,
  width,
  height,
  decorative = false,
}: GovernedCandidateLogoProps) {
  const boundary = useExperienceModeBoundary();
  const effectiveRenderMode = boundary.mode;
  const candidateAllowed = canRenderGovernedCandidateLogo(boundary.governed, process.env.NODE_ENV);
  const [failedSource, setFailedSource] = useState<string | null>(null);

  if (!candidateAllowed) {
    return (
      <span
        data-candidate-logo-refused={boundary.governed ? 'production' : 'missing_governed_boundary'}
      >
        <Logo
          variant={variant}
          className={className}
          width={width}
          height={height}
          decorative={decorative}
        />
      </span>
    );
  }

  const manifestVariant = resolveCandidateLogoVariant(variant, effectiveRenderMode);
  const asset = EXPERIENCE_BROWSER_BRAND_ASSETS.variants[manifestVariant];
  const size = resolveLogoSize(manifestVariant, width, height);
  const failed = failedSource === asset.dataUri;
  const alternativeText = accessibleText(asset, decorative);

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center [forced-color-adjust:auto]',
        className,
      )}
      style={{ padding: size.clearSpacePx }}
      data-brand-asset-revision={EXPERIENCE_BROWSER_BRAND_ASSETS.assetRevision}
      data-brand-asset-sha256={asset.sha256}
      data-brand-variant={manifestVariant}
      data-brand-render-mode="candidate"
      data-brand-experience-mode={effectiveRenderMode}
      data-brand-clear-space-px={String(size.clearSpacePx)}
    >
      {failed ? (
        decorative ? null : (
          <span
            role="img"
            aria-label={asset.accessibility.standaloneAccessibleName}
            className="experience-candidate-heading inline-flex items-center justify-center font-heading font-bold text-text-heading"
            style={{ width: size.width, height: size.height }}
            data-logo-fallback="text"
          >
            {asset.accessibility.imageFailureFallbackText}
          </span>
        )
      ) : (
        <img
          src={asset.dataUri}
          alt={alternativeText}
          aria-hidden={decorative || undefined}
          width={size.width}
          height={size.height}
          onError={() => setFailedSource(asset.dataUri)}
          style={{
            width: size.width,
            height: size.height,
            objectFit: 'contain',
          }}
        />
      )}
    </span>
  );
}
